/**
 *
 * DataTable with Virtualization - Fixed Version
 *
 */
import * as React from 'react';
import { SetStateAction, useEffect, useMemo, useState, useCallback, useRef } from 'react';
import TableContainer from '@mui/material/TableContainer';
import { ToolBar } from '../ToolBar';
import Box from '@mui/material/Box';
import { DivWrap } from '../DivWrap';
import useHasDate from '../../../utils/customHooks/useHasDate';
import { DataTemplate } from '../../../utils/types/dataTemplate';
import { LinearProgress, Table } from '@mui/material';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import LTableHead from './LTableHead';
import TableBody from '@mui/material/TableBody';
import LTableCell from './LTableCell';
import { v4 as uuidv4 } from 'uuid';
import TableCell from '@mui/material/TableCell';
import { VariableSizeList as List } from 'react-window';

interface Props {
  data: Array<any>;
  loading: boolean;
  error: unknown;
  isError?: boolean;
  template: DataTemplate[];
  title: string;
  fieldFilter?: string;
  setFieldFilter?: React.Dispatch<SetStateAction<string | undefined>>;
  search?: string;
  setSearch?: React.Dispatch<SetStateAction<string>>;
  timeEnd?: number | null;
  setTimeEnd?: React.Dispatch<SetStateAction<number | null>>;
  timeStart?: number | null;
  setTimeStart?: React.Dispatch<SetStateAction<number | null>>;
  order?: 'asc' | 'desc';
  setOrder?: React.Dispatch<SetStateAction<'asc' | 'desc'>>;
  orderBy?: any;
  setOrderBy?: React.Dispatch<SetStateAction<any>>;
  hasImport?: boolean;
  hasRoute?: string;
  noAddButton?: boolean;
  noSearchPanel?: boolean;
  noFilterPanel?: boolean;
  routeEdit?: string;
  totalNumber?: React.ReactNode;
  uuid?: React.ReactNode;
  handleSubmit?: (row: any) => Promise<void>;
  minRowHeight?: number;
  maxHeight?: number;
  currentPageExternal?: number;
  setCurrentPageExternal?: React.Dispatch<SetStateAction<number>>;
  rowsPerPageExternal?: number;
  totalCountExternal?: number;
  disableClientPagination?: boolean;
}

// Virtualized Row Component
interface VirtualRowProps {
  index: number;
  style: React.CSSProperties;
  data: {
    items: Array<any>;
    template: DataTemplate[];
    routeEdit?: string;
    getRowHeight: (index: number) => number;
    setRowHeight: (index: number, height: number) => void;
  };
}

const VirtualRow: React.FC<VirtualRowProps> = ({ index, style, data }) => {
  const { items, template, routeEdit, setRowHeight } = data;
  const row = items[index];
  const rowRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (rowRef.current) {
      const height = rowRef.current.getBoundingClientRect().height;
      setRowHeight(index, height);
    }
  }, [index, setRowHeight, row]);

  return (
    <div style={style}>
      <div
        ref={rowRef}
        style={{
          display: 'flex',
          width: '100%',
          minHeight: 'fit-content',
          borderBottom: '1px solid rgba(224, 224, 224, 1)',
        }}
      >
        {template.map(
          (item, templateIndex) =>
            !item.hide && (
              <div
                key={uuidv4()}
                style={{
                  flex: item.flex || 1,
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0px 0px', // Match MUI TableCell default padding
                  minHeight: '53px', // Match MUI TableCell default height
                  boxSizing: 'border-box',
                }}
              >
                <LTableCell
                  templates={template}
                  templateItem={item}
                  data={row}
                  routeEdit={routeEdit}
                />
              </div>
            ),
        )}
      </div>
    </div>
  );
};

export function DataTable({
  data,
  loading,
  error,
  isError,
  template,
  title,
  hasImport,
  hasRoute,
  routeEdit,
  fieldFilter,
  search,
  setSearch,
  setFieldFilter,
  timeStart,
  setTimeStart,
  timeEnd,
  setTimeEnd,
  order,
  setOrder,
  orderBy,
  setOrderBy,
  noAddButton,
  noSearchPanel,
  noFilterPanel,
  totalNumber,
  uuid,
  handleSubmit,
  minRowHeight = 70,
  maxHeight = 700,
  currentPageExternal,
  setCurrentPageExternal,
  rowsPerPageExternal,
  totalCountExternal,
  disableClientPagination = false,
}: Props) {
  const [rowsPerPage, setRowsPerPage] = React.useState(5000);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPage, setTotalPage] = useState(0);
  
  // Store row heights for dynamic sizing
  const rowHeights = useRef<{ [key: number]: number }>({});
  const listRef = useRef<any>(null);

  const fieldsDate = useHasDate(template);
  const checkSortLocal = template
    .map(item => item.hasOwnProperty('sort'))
    .some(item => item);
  const checkSearchLocal = template
    .map(item => item.hasOwnProperty('filterField'))
    .some(item => item);

  // select filter and pagination
  useEffect(() => {
    if (disableClientPagination) {
      return;
    }
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [disableClientPagination, currentPage, search]);

  const result: Array<any> = useMemo(() => {
    if (data === null) {
      data = [];
    }

    let temp = [...data];

    if (timeEnd && timeStart && fieldsDate.start !== '' && !checkSearchLocal) {
      temp = data.filter(item => {
        const date = new Date(item[fieldsDate.start]).getTime();
        return timeStart <= date && timeEnd >= date;
      });
    }

    if (search && search !== '' && !checkSearchLocal) {
      temp = data.filter(item => {
        if (fieldFilter && search) {
          if (typeof item[fieldFilter] === 'number') {
            return item[fieldFilter] === +search;
          }
          return item[fieldFilter].toLowerCase().includes(search.toLowerCase());
        }
      });
    }

    if (orderBy !== null && !checkSortLocal) {
      temp.sort((a, b) => {
        if (b[orderBy] < a[orderBy]) {
          return order === 'asc' ? -1 : 1;
        }
        if (b[orderBy] > a[orderBy]) {
          return order === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    setTotalPage(disableClientPagination ? totalCountExternal || 0 : temp.length);
    return temp;
  }, [
    data,
    disableClientPagination,
    orderBy,
    order,
    fieldFilter,
    search,
    totalCountExternal,
    timeEnd,
    timeStart,
  ]);

  const paginatedData = useMemo(() => {
    if (disableClientPagination) {
      return result;
    }
    const firstPageIndex = (currentPage - 1) * rowsPerPage;
    const lastPageIndex = firstPageIndex + rowsPerPage;
    return result.slice(firstPageIndex, lastPageIndex);
  }, [disableClientPagination, result, currentPage, rowsPerPage]);

  // Reset row heights when data changes
  useEffect(() => {
    rowHeights.current = {};
    if (listRef.current) {
      listRef.current.resetAfterIndex(0);
    }
  }, [paginatedData]);

  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    if (setOrder) {
      setOrder(isAsc ? 'desc' : 'asc');
    }
    if (setOrderBy) {
      setOrderBy(property);
    }
  };

  // Function to get row height
  const getRowHeight = useCallback((index: number) => {
    return rowHeights.current[index] || minRowHeight;
  }, [minRowHeight]);

  // Function to set row height
  const setRowHeight = useCallback((index: number, height: number) => {
    if (rowHeights.current[index] !== height) {
      rowHeights.current[index] = height;
      if (listRef.current) {
        listRef.current.resetAfterIndex(index);
      }
    }
  }, []);

  // Virtualized list item data
  const listData = {
    items: paginatedData,
    template,
    routeEdit,
    getRowHeight,
    setRowHeight,
  };

  const toolbarCurrentPage = disableClientPagination
    ? currentPageExternal
    : currentPage;
  const toolbarSetCurrentPage = disableClientPagination
    ? setCurrentPageExternal
    : setCurrentPage;
  const toolbarRowsPerPage = disableClientPagination
    ? rowsPerPageExternal
    : rowsPerPage;

  return (
    <Box sx={{ overflow: 'hidden' }}>
      <ToolBar
        title={title}
        totalPage={totalPage}
        rowPerPage={toolbarRowsPerPage}
        currentPage={toolbarCurrentPage}
        setCurrentPage={toolbarSetCurrentPage}
        template={template}
        handleSelectField={setFieldFilter}
        handleSearch={setSearch}
        fieldFilter={fieldFilter}
        search={search}
        setTimeStart={setTimeStart}
        setTimeEnd={setTimeEnd}
        hasImport={hasImport}
        hasRoute={hasRoute}
        noAddButton={noAddButton}
        totalNumber={`総数: ${totalPage}`}
        noSearchPanel={noSearchPanel}
      />
      <DivWrap>
        {/* user point history */ uuid && uuid}
        <TableContainer
          sx={{
            maxHeight: maxHeight,
            ['msOverflowStyle']: 'none',
            ['::-webkit-scrollbar']: {
              display: 'none',
            },
          }}
        >
          {loading ? (
            <LinearProgress />
          ) : (
            <Table stickyHeader={true} aria-label="simple table">
              {/* Fixed Table Header */}
              <TableHead>
                <TableRow sx={{ display: 'flex', width: '100%' }}>
                  {template.map(
                    (item, index) =>
                      !item.hide && (
                        <TableCell
                          key={uuidv4()}
                          sx={{
                            flex: item.flex || 1,
                            display: 'flex',
                            alignItems: 'center',
                            padding: '0px 0px', // Match body cell padding
                            minHeight: '53px', // Match body cell height
                            boxSizing: 'border-box',
                            border: 'none',
                            borderBottom: '1px solid rgba(224, 224, 224, 1)',
                          }}
                        >
                          <LTableHead
                            sort={item?.sort}
                            sortName={item?.sortName}
                            checkSortLocal={checkSortLocal}
                            field={item.field}
                            headerName={item.headerName}
                            orderBy={orderBy}
                            order={order}
                            minWidth={item.minWidth}
                            createSortHandler={handleRequestSort}
                          />
                        </TableCell>
                      ),
                  )}
                </TableRow>
              </TableHead>
              
              {/* Virtualized Table Body */}
              {paginatedData.length !== 0 && !isError ? (
                <TableBody component="div" sx={{ width: '100%' }}>
                  <List
                    ref={listRef}
                    height={Math.min(
                      Object.values(rowHeights.current).reduce((sum, height) => sum + height, 0) || 
                      paginatedData.length * minRowHeight,
                      maxHeight
                    )}
                    itemCount={paginatedData.length}
                    itemSize={getRowHeight}
                    itemData={listData}
                    width="100%"
                  >
                    {VirtualRow}
                  </List>
                </TableBody>
              ) : (
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={1000}>データがありません。</TableCell>
                  </TableRow>
                </TableBody>
              )}
            </Table>
          )}
        </TableContainer>
      </DivWrap>
    </Box>
  );
}
